import { SamplePollLanguage } from '../poll/header.class';
import {
    DEFAULT_BRAND,
    DESCRIPTION_MAX,
    SAMPLE_POLL_LIST_META_BY_LANG,
    SAMPLE_POLL_LIST_SEGMENT_BY_LANG,
    SAMPLE_POLL_SEO_BY_LANG,
    TITLE_MAX_BEFORE_BRAND,
} from './sample-poll-seo.constants';

/**
 * Header-Felder, die in Meta-Tags einfliessen duerfen. Strukturell kompatibel
 * zu `HeaderClass` — bewusst nur diese drei Felder (Privacy: Vote-Mode nutzt
 * davon nur pollTitle + pollDescription).
 */
export interface PollMetaInput {
    pollTitle?: string;
    pollDescription?: string;
    longDescription?: string;
}

export interface PollMeta {
    title: string;
    description: string;
}

export type PollMetaMode = 'samplePoll' | 'vote';

/**
 * Kuerzt einen String auf maxLen Zeichen, schneidet am letzten Wort-Boundary
 * innerhalb des Budgets. Kein „..." angehaengt — Google haengt selbst „…" an
 * wenn es die Description rendert. Fallback: harter Schnitt wenn kein Space
 * im sinnvollen Bereich gefunden wird.
 */
export function smartTrimAtWordBoundary(text: string, maxLen: number): string {
    if (text.length <= maxLen) return text;
    const slice = text.substring(0, maxLen);
    const lastSpace = slice.lastIndexOf(' ');
    // Nur cutten wenn der Space nicht zu frueh kommt (vermeidet "Pizza")
    if (lastSpace > maxLen * 0.5) {
        return slice.substring(0, lastSpace).trimEnd();
    }
    return slice.trimEnd();
}

/**
 * Baut Title + Description fuer einen Poll — pure Funktion, genutzt von
 * whocan-app (RouteContextService schreibt das Ergebnis ins DOM) und der
 * ogProxy Cloud Function (patcht das Ergebnis ins Shell-HTML).
 *
 * Mode `samplePoll`:
 *  - Title: "{pollTitle} {titleSuffix}" wenn das in 55 Zeichen passt, sonst
 *    pollTitle alleine, sonst smart-getrimmt; leerer pollTitle -> fallbackTitle.
 *    Immer mit " | {brand}"-Suffix.
 *  - Description-Chain: pollDescription -> longDescription -> fallbackDescription.
 *
 * Mode `vote` (Privacy-Regel):
 *  - Nur pollTitle + pollDescription, KEIN longDescription, keine
 *    Template-Fallback-Strings, kein titleSuffix.
 *  - Leerer pollTitle -> null (Aufrufer faellt auf generische Tags zurueck).
 *  - Leere Description -> description ist ''.
 */
export function buildPollMeta(
    input: PollMetaInput,
    lang: SamplePollLanguage,
    mode: 'samplePoll',
    brand?: string
): PollMeta;
export function buildPollMeta(
    input: PollMetaInput,
    lang: SamplePollLanguage,
    mode: PollMetaMode,
    brand?: string
): PollMeta | null;
export function buildPollMeta(
    input: PollMetaInput,
    lang: SamplePollLanguage,
    mode: PollMetaMode,
    brand?: string
): PollMeta | null {
    const brandSuffix = ` | ${brand || DEFAULT_BRAND}`;
    const pollTitle = (input?.pollTitle || '').trim();
    const pollDescription = (input?.pollDescription || '').trim();

    if (mode === 'vote') {
        if (pollTitle.length === 0) {
            return null;
        }
        const titlePart = smartTrimAtWordBoundary(pollTitle, TITLE_MAX_BEFORE_BRAND);
        const description = smartTrimAtWordBoundary(pollDescription, DESCRIPTION_MAX);
        return { title: titlePart + brandSuffix, description };
    }

    const seo = SAMPLE_POLL_SEO_BY_LANG[lang] ?? SAMPLE_POLL_SEO_BY_LANG.en;

    // --- Title -----------------------------------------------------
    let titlePart: string;
    if (pollTitle.length === 0) {
        // Kein Poll-Title -> nur Fallback-Title, kein Template-Suffix
        // (waere sonst redundant: "Free Poll Template — Free Poll Template")
        titlePart = smartTrimAtWordBoundary(seo.fallbackTitle, TITLE_MAX_BEFORE_BRAND);
    } else {
        const candidateWithSuffix = `${pollTitle} ${seo.titleSuffix}`;
        if (candidateWithSuffix.length <= TITLE_MAX_BEFORE_BRAND) {
            // Suffix passt rein -> mehr Keyword-Match
            titlePart = candidateWithSuffix;
        } else if (pollTitle.length <= TITLE_MAX_BEFORE_BRAND) {
            // Suffix wuerde sprengen, Title alleine passt
            titlePart = pollTitle;
        } else {
            // Selbst Title alleine zu lang -> smart trimmen
            titlePart = smartTrimAtWordBoundary(pollTitle, TITLE_MAX_BEFORE_BRAND);
        }
    }

    // --- Description ----------------------------------------------
    const longDescription = (input?.longDescription || '').trim();
    let description = pollDescription || longDescription || seo.fallbackDescription;
    description = smartTrimAtWordBoundary(description, DESCRIPTION_MAX);

    return { title: titlePart + brandSuffix, description };
}

/**
 * Title + Description der Sample-Poll-Listing-Page — pure Funktion, genutzt
 * von whocan-app (RouteContextService) und dem samplePollList-Zweig der
 * ogProxy Cloud Function, damit Client und Function identische Tags erzeugen.
 * Quelle sind H1 (`samplePoll.list.title`) und Intro (`samplePoll.list.intro`)
 * der Page, mit denselben Budgets wie die Poll-Metas.
 */
export function buildSamplePollListMeta(lang: SamplePollLanguage, brand?: string): PollMeta {
    const meta = SAMPLE_POLL_LIST_META_BY_LANG[lang] ?? SAMPLE_POLL_LIST_META_BY_LANG.en;
    const titlePart = smartTrimAtWordBoundary(meta.title, TITLE_MAX_BEFORE_BRAND);
    return {
        title: titlePart + ` | ${brand || DEFAULT_BRAND}`,
        description: smartTrimAtWordBoundary(meta.description, DESCRIPTION_MAX),
    };
}

/**
 * Kanonischer Pfad einer Sample-Poll-Detailseite, z.B.
 * `/de/umfrage-vorlagen/babyparty`. Host haengt der Aufrufer davor.
 */
export function buildSamplePollPath(lang: SamplePollLanguage, slug: string): string {
    const segment = SAMPLE_POLL_LIST_SEGMENT_BY_LANG[lang] ?? SAMPLE_POLL_LIST_SEGMENT_BY_LANG.en;
    return '/' + lang + '/' + segment + '/' + slug;
}

/**
 * Kanonischer Pfad der Listing-Page, z.B. `/de/umfrage-vorlagen`.
 * Host haengt der Aufrufer davor.
 */
export function buildSamplePollListPath(lang: SamplePollLanguage): string {
    const segment = SAMPLE_POLL_LIST_SEGMENT_BY_LANG[lang] ?? SAMPLE_POLL_LIST_SEGMENT_BY_LANG.en;
    return '/' + lang + '/' + segment;
}
